"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import couponBgImage from "@/assets/images/coupon/coupon_bg.webp";
import { Button } from "@/components/ui/button";
import { cn, formatPrice, getCurrencySymbol } from "@/lib/utils";
import { getCurrentLanguageInClient, getValueFromTranslations } from "@/lib/utils.client";
import CouponQrDialog from "../coupon-qr-dialog";
import CouponRulesDialog from "../coupon-rules-dialog";
import type { UserCoupon, UserCouponWithTemplate } from "../schema";
import { isWebCouponAvailable } from "../services/coupon-calculator";
import { isCouponActive, isCouponApplicableForStore, isCouponApplicableForWeb, isCouponAppOnly, isCouponDisabled, isCouponNotStarted } from "../services/coupon-validator";
import { getCouponTimeDisplayText } from "../utils/coupon-time-display";
export interface CouponCardProps {
  coupon: UserCouponWithTemplate;
  className?: string;
  variant?: "default" | "checkout";
  onShopNow?: (coupon: UserCoupon) => void;
  onSelect?: (coupon: UserCoupon) => void;
  showActions?: boolean;
  showRules?: boolean;
  cartSubtotal?: number; // For minimum order validation in checkout context
  isFromHistoryTab?: boolean; // Whether this coupon is from the history/expired tab
}

/**
 * Unified Coupon Card Component
 * Handles all coupon display variants across the application
 */
export const CouponCard: React.FC<CouponCardProps> = t0 => {
  const $ = _c(131);
  const {
    coupon,
    className,
    variant: t1,
    onShopNow,
    onSelect,
    showActions: t2,
    showRules: t3,
    cartSubtotal,
    isFromHistoryTab: t4
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const showActions = t2 === undefined ? true : t2;
  const showRules = t3 === undefined ? true : t3;
  const isFromHistoryTab = t4 === undefined ? false : t4;
  const lang = getCurrentLanguageInClient();
  const {
    t
  } = useTranslation();
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const title = getValueFromTranslations(lang, coupon.couponTitle_trans) || t("coupon.defaultTitle");
  const t5 = coupon.couponTemplate?.startTime;
  const t6 = coupon.couponTemplate?.endTime;
  let t7;
  if ($[0] !== coupon.expiresAt || $[1] !== t || $[2] !== t5 || $[3] !== t6) {
    t7 = getCouponTimeDisplayText({
      startTime: t5,
      endTime: t6,
      expiresAt: coupon.expiresAt
    }, t);
    $[0] = coupon.expiresAt;
    $[1] = t;
    $[2] = t5;
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const validPeriod = t7;
  let t8;
  if ($[5] !== t) {
    t8 = t("coupon.noThreshold");
    $[5] = t;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let usageCondition = t8;
  if (coupon.appliedDiscountRule.minAmount && coupon.appliedDiscountRule.minAmount > 0) {
    let t9;
    if ($[7] !== coupon.appliedDiscountRule.currency || $[8] !== coupon.appliedDiscountRule.minAmount || $[9] !== t) {
      t9 = t("coupon.minAmount", {
        symbol: getCurrencySymbol(coupon.appliedDiscountRule.currency),
        amount: formatPrice(coupon.appliedDiscountRule.currency, coupon.appliedDiscountRule.minAmount)
      });
      $[7] = coupon.appliedDiscountRule.currency;
      $[8] = coupon.appliedDiscountRule.minAmount;
      $[9] = t;
      $[10] = t9;
    } else {
      t9 = $[10];
    }
    usageCondition = t9;
  }
  let T0;
  let getActionButton;
  let getInfoClasses;
  let getTitleClasses;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t9;
  if ($[11] !== cartSubtotal || $[12] !== className || $[13] !== coupon || $[14] !== isFromHistoryTab || $[15] !== onSelect || $[16] !== onShopNow || $[17] !== setQrDialogOpen || $[18] !== showActions || $[19] !== t || $[20] !== variant) {
    const isApp = isCouponAppOnly(coupon);
    let t17;
    if ($[33] !== coupon) {
      t17 = isCouponNotStarted(coupon);
      $[33] = coupon;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    const isNotStarted = t17;
    const isDisabled = isCouponDisabled(coupon);
    let t18;
    if ($[35] !== cartSubtotal || $[36] !== coupon || $[37] !== variant) {
      t18 = variant === "checkout" && cartSubtotal !== undefined && !isWebCouponAvailable(coupon, cartSubtotal);
      $[35] = cartSubtotal;
      $[36] = coupon;
      $[37] = variant;
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    const isNotAvailableForCheckout = t18;
    const isNotApplicable = isDisabled || isApp || isNotAvailableForCheckout;
    let t19;
    if ($[39] !== coupon.status || $[40] !== isNotAvailableForCheckout || $[41] !== isNotStarted || $[42] !== t) {
      t19 = () => {
        if (isNotStarted) {
          return t("coupon.notStarted");
        }
        if (coupon.status === "used") {
          return t("coupon.used");
        }
        if (coupon.status === "expired") {
          return t("coupon.expired");
        }
        if (isNotAvailableForCheckout) {
          return t("coupon.unavailable");
        }
        return null;
      };
      $[39] = coupon.status;
      $[40] = isNotAvailableForCheckout;
      $[41] = isNotStarted;
      $[42] = t;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    const getStatusText = t19;
    getActionButton = () => {
      if (!showActions || !isCouponActive(coupon) || isNotStarted) {
        if (isFromHistoryTab && (coupon.status === "used" || coupon.status === "expired")) {
          return <Button variant="default" size="sm" disabled={true} className="h-7 min-w-14 cursor-not-allowed bg-gray-200 px-3 py-1 font-['DINPro-Medium'] text-xs text-gray-400 uppercase md:h-8 md:min-w-20 md:text-sm">{getStatusText()}</Button>;
        }
        if (isApp) {
          return <Button variant="default" size="sm" disabled={true} className="h-7 min-w-14 cursor-not-allowed bg-gray-200 px-3 py-1 font-['DINPro-Medium'] text-xs text-gray-400 uppercase md:h-8 md:min-w-20 md:text-sm">{t("coupon.appOnly")}</Button>;
        }
        if (isNotAvailableForCheckout) {
          return <Button variant="default" size="sm" disabled={true} className="h-7 min-w-14 cursor-not-allowed bg-gray-200 px-3 py-1 font-['DINPro-Medium'] text-xs text-gray-400 uppercase md:h-8 md:min-w-20 md:text-sm">{t("coupon.unavailable")}</Button>;
        }
        if (isDisabled) {
          return <Button variant="default" size="sm" disabled={true} className="h-7 min-w-14 cursor-not-allowed bg-gray-200 px-3 py-1 font-['DINPro-Medium'] text-xs text-gray-400 uppercase md:h-8 md:min-w-20 md:text-sm">{getStatusText()}</Button>;
        }
        return null;
      }
      if (isCouponApplicableForStore(coupon)) {
        return <Button onClick={() => setQrDialogOpen(true)} variant="default" size="sm" className="h-7 min-w-14 px-3 py-1 text-xs uppercase md:h-8 md:min-w-20 md:text-sm">{t("coupon.showCode")}</Button>;
      }
      if (isApp) {
        return <Button variant="default" size="sm" disabled={true} className="h-7 min-w-14 cursor-not-allowed bg-gray-200 px-3 py-1 font-['DINPro-Medium'] text-xs text-gray-400 uppercase md:h-8 md:min-w-20 md:text-sm">{t("coupon.appOnly")}</Button>;
      }
      if (isNotAvailableForCheckout) {
        return <Button variant="default" size="sm" disabled={true} className="h-7 min-w-14 cursor-not-allowed bg-gray-200 px-3 py-1 font-['DINPro-Medium'] text-xs text-gray-400 uppercase md:h-8 md:min-w-20 md:text-sm">{t("coupon.unavailable")}</Button>;
      }
      if (variant === "checkout" && onSelect) {
        return null;
      }
      return <Button onClick={() => onShopNow?.(coupon)} variant="default" size="sm" disabled={!isCouponApplicableForWeb(coupon)} className="h-7 min-w-14 px-3 py-1 font-['DINPro-Medium'] text-sm uppercase md:h-8 md:min-w-20 md:text-sm">{t("coupon.shopNow")}</Button>;
    };
    let t20;
    if ($[44] !== className || $[45] !== variant) {
      t20 = () => {
        switch (variant) {
          case "checkout":
            {
              return cn("relative flex w-full justify-center overflow-hidden", "h-44 md:h-48", className);
            }
          default:
            {
              return cn("relative flex w-full justify-center overflow-hidden", "h-44 md:h-48", className);
            }
        }
      };
      $[44] = className;
      $[45] = variant;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    const getContainerClasses = t20;
    let t21;
    if ($[47] !== variant) {
      t21 = () => {
        switch (variant) {
          case "checkout":
            {
              return cn("relative flex w-full flex-col justify-between rounded-lg bg-white p-2 shadow-md", "h-44 md:h-48 md:max-w-xl");
            }
          default:
            {
              return cn("relative flex w-full flex-col justify-between rounded-lg bg-white p-2 shadow-md", "h-44 md:h-48 md:max-w-xl");
            }
        }
      };
      $[47] = variant;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    const getContentClasses = t21;
    let t22;
    if ($[49] !== variant) {
      t22 = () => {
        switch (variant) {
          case "checkout":
            {
              return cn("text-left font-['DINPro-Medium'] font-medium text-black mb-2 leading-tight truncate w-full", "text-base md:text-lg lg:text-xl");
            }
          default:
            {
              return cn("text-left font-['DINPro-Medium'] font-medium text-black mb-2 leading-tight truncate w-full", "text-base md:text-lg lg:text-xl");
            }
        }
      };
      $[49] = variant;
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    getTitleClasses = t22;
    let t23;
    if ($[51] !== variant) {
      t23 = () => {
        switch (variant) {
          case "checkout":
            {
              return cn("space-y-1 text-left font-['DINPro-Regular'] text-sm font-normal text-[#333]", "text-xs md:text-base");
            }
          default:
            {
              return cn("space-y-1 text-left font-['DINPro-Regular'] text-sm font-normal text-[#333]", "text-xs md:text-base");
            }
        }
      };
      $[51] = variant;
      $[52] = t23;
    } else {
      t23 = $[52];
    }
    getInfoClasses = t23;
    if ($[53] !== getContainerClasses) {
      t14 = getContainerClasses();
      $[53] = getContainerClasses;
      $[54] = t14;
    } else {
      t14 = $[54];
    }
    t15 = () => {
      if (variant === "checkout" && onSelect && !isNotApplicable) {
        onSelect(coupon);
      }
    };
    const t24 = variant === "checkout" && onSelect && !isNotApplicable ? "pointer" : "default";
    if ($[55] !== t24) {
      t16 = {
        cursor: t24
      };
      $[55] = t24;
      $[56] = t16;
    } else {
      t16 = $[56];
    }
    if ($[57] !== getContentClasses) {
      t13 = getContentClasses();
      $[57] = getContentClasses;
      $[58] = t13;
    } else {
      t13 = $[58];
    }
    T0 = Image;
    t9 = couponBgImage;
    t10 = "Coupon background";
    t11 = true;
    t12 = cn("z-0 object-cover object-left", isNotApplicable && "opacity-80 grayscale");
    $[11] = cartSubtotal;
    $[12] = className;
    $[13] = coupon;
    $[14] = isFromHistoryTab;
    $[15] = onSelect;
    $[16] = onShopNow;
    $[17] = setQrDialogOpen;
    $[18] = showActions;
    $[19] = t;
    $[20] = variant;
    $[21] = T0;
    $[22] = getActionButton;
    $[23] = getInfoClasses;
    $[24] = getTitleClasses;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t9;
  } else {
    T0 = $[21];
    getActionButton = $[22];
    getInfoClasses = $[23];
    getTitleClasses = $[24];
    t10 = $[25];
    t11 = $[26];
    t12 = $[27];
    t13 = $[28];
    t14 = $[29];
    t15 = $[30];
    t16 = $[31];
    t9 = $[32];
  }
  let t17;
  if ($[59] !== T0 || $[60] !== t10 || $[61] !== t11 || $[62] !== t12 || $[63] !== t9) {
    t17 = <T0 src={t9} alt={t10} fill={t11} className={t12} priority={true} unoptimized={true} />;
    $[59] = T0;
    $[60] = t10;
    $[61] = t11;
    $[62] = t12;
    $[63] = t9;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== getTitleClasses) {
    t18 = cn(getTitleClasses(), "mx-4");
    $[65] = getTitleClasses;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== t18 || $[68] !== title) {
    t19 = <h3 className={t18} title={title}>{title}</h3>;
    $[67] = t18;
    $[68] = title;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  let t20;
  if ($[70] !== getInfoClasses) {
    t20 = cn(getInfoClasses(), "mx-4");
    $[70] = getInfoClasses;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== validPeriod) {
    t21 = validPeriod && <span className="inline-block w-0.5 h-0.5 bg-gray-700 rounded-full mr-2 shrink-0" />;
    $[72] = validPeriod;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  const t22 = validPeriod || "\xA0";
  let t23;
  if ($[74] !== t22) {
    t23 = <span className="truncate">{t22}</span>;
    $[74] = t22;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] !== t21 || $[77] !== t23) {
    t24 = <div className="flex items-center text-xs md:text-sm">{t21}{t23}</div>;
    $[76] = t21;
    $[77] = t23;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== usageCondition) {
    t25 = usageCondition && <span className="inline-block w-0.5 h-0.5 bg-gray-700 rounded-full mr-2 shrink-0" />;
    $[79] = usageCondition;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  const t26 = usageCondition || "\xA0";
  let t27;
  if ($[81] !== t26) {
    t27 = <span className="truncate">{t26}</span>;
    $[81] = t26;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== t25 || $[84] !== t27) {
    t28 = <div className="flex items-center text-xs md:text-sm">{t25}{t27}</div>;
    $[83] = t25;
    $[84] = t27;
    $[85] = t28;
  } else {
    t28 = $[85];
  }
  let t29;
  if ($[86] !== t20 || $[87] !== t24 || $[88] !== t28) {
    t29 = <div className={t20}>{t24}{t28}</div>;
    $[86] = t20;
    $[87] = t24;
    $[88] = t28;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== t19 || $[91] !== t29) {
    t30 = <div className="px-4 pt-[24px] md:pt-6 pb-6">{t19}{t29}</div>;
    $[90] = t19;
    $[91] = t29;
    $[92] = t30;
  } else {
    t30 = $[92];
  }
  let t31;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="pointer-events-none flex w-full items-center px-6"><div className="ml-2 h-px flex-1 border-t border-dashed border-gray-300" /></div>;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  let t32;
  if ($[94] !== setRulesDialogOpen || $[95] !== showRules || $[96] !== t) {
    t32 = showRules && <button type="button" className={cn("text-left font-['DINPro-Regular'] text-base font-normal uppercase underline", "cursor-pointer text-[#333] hover:text-[#666]")} onClick={e => {
      e.stopPropagation();
      setRulesDialogOpen(true);
    }}>{t("coupon.rules")}</button>;
    $[94] = setRulesDialogOpen;
    $[95] = showRules;
    $[96] = t;
    $[97] = t32;
  } else {
    t32 = $[97];
  }
  let t33;
  if ($[98] !== getActionButton) {
    t33 = getActionButton();
    $[98] = getActionButton;
    $[99] = t33;
  } else {
    t33 = $[99];
  }
  let t34;
  if ($[100] !== t33) {
    t34 = <div>{t33}</div>;
    $[100] = t33;
    $[101] = t34;
  } else {
    t34 = $[101];
  }
  let t35;
  if ($[102] !== t32 || $[103] !== t34) {
    t35 = <div className="px-4 pt-4 pb-6"><div className="flex flex-wrap items-center justify-end gap-6">{t32}{t34}</div></div>;
    $[102] = t32;
    $[103] = t34;
    $[104] = t35;
  } else {
    t35 = $[104];
  }
  let t36;
  if ($[105] !== t30 || $[106] !== t35) {
    t36 = <div className="absolute inset-0 z-10 flex h-full w-full flex-col">{t30}{t31}{t35}</div>;
    $[105] = t30;
    $[106] = t35;
    $[107] = t36;
  } else {
    t36 = $[107];
  }
  let t37;
  if ($[108] !== t13 || $[109] !== t17 || $[110] !== t36) {
    t37 = <div className={t13}>{t17}{t36}</div>;
    $[108] = t13;
    $[109] = t17;
    $[110] = t36;
    $[111] = t37;
  } else {
    t37 = $[111];
  }
  let t38;
  if ($[112] !== t14 || $[113] !== t15 || $[114] !== t16 || $[115] !== t37) {
    t38 = <div className={t14} onClick={t15} style={t16}>{t37}</div>;
    $[112] = t14;
    $[113] = t15;
    $[114] = t16;
    $[115] = t37;
    $[116] = t38;
  } else {
    t38 = $[116];
  }
  const t39 = coupon.couponTemplate?.code || coupon.couponId;
  let t40;
  if ($[117] !== coupon.couponId || $[118] !== qrDialogOpen || $[119] !== setQrDialogOpen || $[120] !== t39) {
    t40 = <CouponQrDialog open={qrDialogOpen} onOpenChange={setQrDialogOpen} couponId={coupon.couponId} code={t39} />;
    $[117] = coupon.couponId;
    $[118] = qrDialogOpen;
    $[119] = setQrDialogOpen;
    $[120] = t39;
    $[121] = t40;
  } else {
    t40 = $[121];
  }
  const t41 = coupon.couponTemplate?.code || coupon.couponId;
  let t42;
  if ($[122] !== coupon || $[123] !== rulesDialogOpen || $[124] !== setRulesDialogOpen || $[125] !== t41) {
    t42 = <CouponRulesDialog open={rulesDialogOpen} onOpenChange={setRulesDialogOpen} coupon={coupon} code={t41} />;
    $[122] = coupon;
    $[123] = rulesDialogOpen;
    $[124] = setRulesDialogOpen;
    $[125] = t41;
    $[126] = t42;
  } else {
    t42 = $[126];
  }
  let t43;
  if ($[127] !== t38 || $[128] !== t40 || $[129] !== t42) {
    t43 = <>{t38}{t40}{t42}</>;
    $[127] = t38;
    $[128] = t40;
    $[129] = t42;
    $[130] = t43;
  } else {
    t43 = $[130];
  }
  return t43;
};
export default CouponCard;